import { useState, useCallback, useRef } from 'react';

import { JobData } from '../api/JobsService';
import { JobsService } from '../api/JobsService';

/**
 * @param onJobsDiscovered  callback fired with each page of results
 * @param pageSize          rows per page requested from the server. Defaults to
 *                          50 to preserve the behaviour of existing callers
 *                          (Interviews tab job selector); the Jobs tab passes
 *                          its own page size so search pagination matches browse.
 */
export const useJobSearchApi = (
  onJobsDiscovered?: (jobs: JobData[]) => void,
  pageSize: number = 50
) => {
  const [searchResults, setSearchResults] = useState<JobData[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // VI-885 (Finding #2): distinguish a failed search (network/server error) from
  // a genuine zero-result search so the UI can show an error+retry, not "no jobs".
  const [searchError, setSearchError] = useState(false);

  // Remember the active query so page changes re-run the same field-scoped search.
  const lastQueryRef = useRef<{ keyword: string; searchBy?: string } | null>(null);

  const runSearch = useCallback(
    async (keyword: string, searchBy: string | undefined, page: number) => {
      setIsSearching(true);
      setHasSearched(true);
      setSearchError(false);
      try {
        const result = await JobsService.searchJobs(keyword, page, pageSize, searchBy);
        setSearchResults(result.jobs);
        setSearchTotal(result.total);
        setSearchPage(page);
        onJobsDiscovered?.(result.jobs);
      } catch (error: unknown) {
        console.error('Job Manager API search failed:', error);
        setSearchResults([]);
        setSearchTotal(0);
        setSearchPage(page);
        setSearchError(true);
      } finally {
        setIsSearching(false);
      }
    },
    [onJobsDiscovered, pageSize]
  );

  // Start a new search (always resets to page 0). `searchBy` is optional so
  // existing callers (`searchByKeyword(keyword)`) keep the legacy broad search.
  const searchByKeyword = useCallback(
    async (keyword: string, searchBy?: string) => {
      const trimmed = keyword?.trim();
      if (!trimmed) {
        setSearchResults([]);
        setSearchTotal(0);
        setSearchPage(0);
        setHasSearched(false);
        setSearchError(false);
        lastQueryRef.current = null;
        return;
      }
      lastQueryRef.current = { keyword: trimmed, searchBy };
      await runSearch(trimmed, searchBy, 0);
    },
    [runSearch]
  );

  // Re-run the active query for a different page (server-side pagination).
  const goToSearchPage = useCallback(
    async (page: number) => {
      if (!lastQueryRef.current) return;
      await runSearch(lastQueryRef.current.keyword, lastQueryRef.current.searchBy, page);
    },
    [runSearch]
  );

  // VI-885 (Finding #2): retry the last query at the current page after an error.
  const retrySearch = useCallback(async () => {
    if (!lastQueryRef.current) return;
    await runSearch(lastQueryRef.current.keyword, lastQueryRef.current.searchBy, searchPage);
  }, [runSearch, searchPage]);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchTotal(0);
    setSearchPage(0);
    setHasSearched(false);
    setSearchError(false);
    lastQueryRef.current = null;
  }, []);

  return {
    searchResults,
    searchTotal,
    searchPage,
    isSearching,
    hasSearched,
    searchError,
    searchByKeyword,
    goToSearchPage,
    retrySearch,
    clearSearch,
  };
};
